// packages/frontend/src/components/admin/SportSeasonCellModal.tsx
import { useEffect, useState } from 'react';
import { X, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSportSeasonsStore } from '../../store/sportSeasonsStore';
import { useNamedRegionsStore } from '../../store/namedRegionsStore';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { genderLabel } from '../../lib/sportLabel';
import type {
  SportSeasonDefault,
  Season,
  Confidence,
  PropagationResult,
} from '../../types/sportSeason';

const SEASONS: Season[] = ['FALL', 'WINTER', 'SPRING', 'SUMMER'];
const CONFIDENCES: Confidence[] = ['HIGH', 'MEDIUM', 'LOW'];

interface Props {
  cell: SportSeasonDefault;
  onClose: () => void;
}

// The store types `saveCell`'s result as `{ default: unknown; propagation: unknown }`
// (intentionally loose at the store layer). We narrow it here at the call site.
interface SaveCellResult {
  default: SportSeasonDefault;
  propagation: PropagationResult;
}

interface RegionalDraft {
  primarySeason: Season | null;
  confidence: Confidence;
  sanctioned: boolean;
}

const DEFAULT_REGIONAL_DRAFT: RegionalDraft = {
  primarySeason: null,
  confidence: 'MEDIUM',
  sanctioned: true,
};

// Light/dark pill classes per spec §2. Inline because there is one consumer.
const SEASON_PILL_CLASS: Record<Season, string> = {
  FALL: 'bg-orange-100 text-orange-900 dark:bg-orange-500/20 dark:text-orange-300',
  WINTER: 'bg-blue-100 text-blue-900 dark:bg-blue-500/20 dark:text-blue-300',
  SPRING: 'bg-green-100 text-green-900 dark:bg-green-500/20 dark:text-green-300',
  SUMMER: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-500/20 dark:text-yellow-300',
};

function SeasonPill({ season }: { season: Season }) {
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[44px] h-[22px] rounded text-xs font-semibold ${SEASON_PILL_CLASS[season]}`}
    >
      {season === 'FALL' ? 'Fall' : season === 'WINTER' ? 'Win' : season === 'SPRING' ? 'Spr' : 'Sum'}
    </span>
  );
}

export function SportSeasonCellModal({ cell, onClose }: Props) {
  const {
    saveCell,
    fetchRegionalRows,
    createRegionalRow,
    updateRegionalRow,
    deleteRegionalRow,
  } = useSportSeasonsStore();
  const { regions: namedRegions, fetchRegions } = useNamedRegionsStore();

  const [draft, setDraft] = useState({
    primarySeason: cell.primarySeason,
    alsoOffered: cell.alsoOffered,
    sanctioned: cell.sanctioned,
    source: cell.source ?? '',
    confidence: cell.confidence,
    notes: cell.notes ?? '',
  });
  const [confirming, setConfirming] = useState<null | {
    count: number;
    sample: Array<{ organizationName: string }>;
  }>(null);

  // Regional overrides state
  const [regionalRows, setRegionalRows] = useState<SportSeasonDefault[]>([]);
  const [regionalLoading, setRegionalLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<RegionalDraft>(DEFAULT_REGIONAL_DRAFT);
  const [addingRegionId, setAddingRegionId] = useState<string | null>(null);
  const [addingDraft, setAddingDraft] = useState<RegionalDraft>(DEFAULT_REGIONAL_DRAFT);
  const [pendingDelete, setPendingDelete] = useState<SportSeasonDefault | null>(null);

  // Load regional rows + named regions when the modal opens or cell changes.
  useEffect(() => {
    let cancelled = false;
    setRegionalLoading(true);
    (async () => {
      try {
        const [rows] = await Promise.all([
          fetchRegionalRows(cell.sportName, cell.gender, cell.stateCode),
          fetchRegions(cell.stateCode),
        ]);
        if (!cancelled) setRegionalRows(rows);
      } catch (e: unknown) {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : 'Failed to load regional overrides';
          toast.error(message);
        }
      } finally {
        if (!cancelled) setRegionalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cell.sportName, cell.gender, cell.stateCode, fetchRegionalRows, fetchRegions]);

  // Escape key dismisses (when not in a sub-flow).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pendingDelete) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pendingDelete]);

  async function refreshRegional() {
    const rows = await fetchRegionalRows(cell.sportName, cell.gender, cell.stateCode);
    setRegionalRows(rows);
  }

  async function attemptSave() {
    const result = (await saveCell(
      cell.id,
      { ...draft, primarySeason: draft.primarySeason ?? null },
      { dryRun: true },
    )) as SaveCellResult;
    if (result.propagation.updatedCount > 25) {
      setConfirming({
        count: result.propagation.updatedCount,
        sample: result.propagation.sampleAffected,
      });
      return;
    }
    await commit();
  }

  async function commit() {
    const result = (await saveCell(cell.id, {
      ...draft,
      primarySeason: draft.primarySeason ?? null,
    })) as SaveCellResult;
    toast.success(`Updated ${result.propagation.updatedCount} schools in ${cell.stateCode}`);
    onClose();
  }

  function startEditRegional(row: SportSeasonDefault) {
    setAddingRegionId(null);
    setEditingId(row.id);
    setEditingDraft({
      primarySeason: row.primarySeason,
      confidence: row.confidence,
      sanctioned: row.sanctioned,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingDraft(DEFAULT_REGIONAL_DRAFT);
  }

  async function saveEditRegional(id: string) {
    try {
      await updateRegionalRow(id, {
        primarySeason: editingDraft.primarySeason,
        confidence: editingDraft.confidence,
        sanctioned: editingDraft.sanctioned,
      });
      toast.success('Regional override updated');
      cancelEdit();
      await refreshRegional();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to update regional override';
      toast.error(message);
    }
  }

  async function confirmDeleteRegional() {
    if (!pendingDelete) return;
    try {
      await deleteRegionalRow(pendingDelete.id);
      toast.success('Regional override deleted');
      await refreshRegional();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to delete regional override';
      toast.error(message);
    } finally {
      setPendingDelete(null);
    }
  }

  function startAddRegional(regionId: string) {
    setEditingId(null);
    setAddingRegionId(regionId);
    setAddingDraft(DEFAULT_REGIONAL_DRAFT);
  }

  function cancelAdd() {
    setAddingRegionId(null);
    setAddingDraft(DEFAULT_REGIONAL_DRAFT);
  }

  async function saveAddRegional() {
    if (!addingRegionId) return;
    const region = namedRegions.find((r) => r.id === addingRegionId);
    if (!region) return;
    let createdCount = 0;
    try {
      // Create one SportSeasonDefault row per ZIP-3 in the selected NamedRegion.
      // Run sequentially so a partial failure surfaces clearly (rather than a
      // promise.all batch that could leave the user guessing which ZIPs landed).
      for (const z of region.zip3s) {
        await createRegionalRow({
          sportName: cell.sportName,
          gender: cell.gender,
          stateCode: cell.stateCode,
          zip3Prefix: z.zip3,
          primarySeason: addingDraft.primarySeason,
          confidence: addingDraft.confidence,
          sanctioned: addingDraft.sanctioned,
        });
        createdCount++;
      }
      toast.success(
        `Added regional override for ${region.name} (${region.zip3s.length} ZIP-3${region.zip3s.length === 1 ? '' : 's'})`,
      );
      cancelAdd();
      await refreshRegional();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to add regional override';
      toast.error(
        `Failed after creating ${createdCount} of ${region.zip3s.length} ZIP-3 override${region.zip3s.length === 1 ? '' : 's'} for ${region.name}: ${message}`,
      );
      // Surface partial state immediately so the user can see what was saved
      // without having to close and reopen the modal.
      await refreshRegional();
    }
  }

  // NamedRegions that haven't already been used as overrides for this cell.
  const availableRegions = namedRegions.filter(
    (nr) => !regionalRows.some((rr) => nr.zip3s.some((z) => z.zip3 === rr.zip3Prefix)),
  );

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-gray-900 rounded-lg max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">
              {[cell.sportName, genderLabel(cell.gender), cell.stateCode].filter(Boolean).join(' · ')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 overflow-y-auto flex-1">
            {confirming ? (
              // Dry-run impact preview replaces the form section when active.
              <div className="rounded-md border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-sm text-amber-900 dark:text-amber-200 mb-2">
                  This will change the season at <b>{confirming.count}</b> schools in {cell.stateCode}:
                </p>
                <ul className="text-xs text-amber-800 dark:text-amber-300 mb-3 list-disc pl-4">
                  {confirming.sample.map((s, i) => (
                    <li key={i}>{s.organizationName}</li>
                  ))}
                </ul>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirming(null)}
                  >
                    Cancel
                  </button>
                  <button type="button" className="btn-primary" onClick={commit}>
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Section A: State-wide default */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="season-primary" className="label">Primary season</label>
                    <select
                      id="season-primary"
                      className="input"
                      value={draft.primarySeason ?? ''}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          primarySeason: (e.target.value || null) as Season | null,
                        })
                      }
                    >
                      <option value="">(none)</option>
                      {SEASONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="season-confidence" className="label">Confidence</label>
                    <select
                      id="season-confidence"
                      className="input"
                      value={draft.confidence}
                      onChange={(e) => setDraft({ ...draft, confidence: e.target.value as Confidence })}
                    >
                      {CONFIDENCES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <span className="label">Also offered</span>
                    <div className="flex gap-4 mt-1">
                      {SEASONS.map((s) => (
                        <label
                          key={s}
                          className="text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-1"
                        >
                          <input
                            type="checkbox"
                            checked={draft.alsoOffered.includes(s)}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                alsoOffered: e.target.checked
                                  ? [...draft.alsoOffered, s]
                                  : draft.alsoOffered.filter((x) => x !== s),
                              })
                            }
                          />
                          {s}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={draft.sanctioned}
                        onChange={(e) => setDraft({ ...draft, sanctioned: e.target.checked })}
                      />
                      Sanctioned
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="season-source" className="label">Source URL</label>
                    <input
                      id="season-source"
                      type="text"
                      className="input"
                      value={draft.source}
                      onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="season-notes" className="label">Notes</label>
                    <textarea
                      id="season-notes"
                      className="input"
                      rows={2}
                      value={draft.notes}
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    />
                  </div>
                </div>

                {/* Section B: Regional overrides */}
                <hr className="my-4 border-gray-200 dark:border-gray-700" />
                <h3 className="text-sm font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  Regional overrides
                </h3>

                {regionalLoading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">Loading…</p>
                )}

                {!regionalLoading && regionalRows.length === 0 && namedRegions.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No regions defined for {cell.stateCode}.{' '}
                    <a
                      href="/admin/regions"
                      className="text-blast-600 dark:text-blast-400 hover:underline"
                    >
                      Define regions →
                    </a>
                  </p>
                )}

                {regionalRows.map((row) => {
                  const region = namedRegions.find((r) =>
                    r.zip3s.some((z) => z.zip3 === row.zip3Prefix),
                  );
                  const label = region?.name ?? `ZIP-3 ${row.zip3Prefix}`;
                  if (editingId === row.id) {
                    return (
                      <div
                        key={row.id}
                        className="rounded-md border border-gray-200 dark:border-gray-700 p-3 mb-2"
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Editing: {label}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label htmlFor={`override-${row.id}-season`} className="label text-xs">Season</label>
                            <select
                              id={`override-${row.id}-season`}
                              className="input"
                              value={editingDraft.primarySeason ?? ''}
                              onChange={(e) =>
                                setEditingDraft({
                                  ...editingDraft,
                                  primarySeason: (e.target.value || null) as Season | null,
                                })
                              }
                            >
                              <option value="">(none)</option>
                              {SEASONS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor={`override-${row.id}-confidence`} className="label text-xs">Confidence</label>
                            <select
                              id={`override-${row.id}-confidence`}
                              className="input"
                              value={editingDraft.confidence}
                              onChange={(e) =>
                                setEditingDraft({
                                  ...editingDraft,
                                  confidence: e.target.value as Confidence,
                                })
                              }
                            >
                              {CONFIDENCES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={editingDraft.sanctioned}
                                onChange={(e) =>
                                  setEditingDraft({
                                    ...editingDraft,
                                    sanctioned: e.target.checked,
                                  })
                                }
                              />
                              Sanctioned
                            </label>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-2">
                          <button type="button" className="btn-secondary" onClick={cancelEdit}>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={() => saveEditRegional(row.id)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={row.id}
                      className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700 first:border-t-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span>
                        {row.primarySeason && <SeasonPill season={row.primarySeason} />}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {row.confidence}
                          {!row.sanctioned ? ' · not sanctioned' : ''}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => startEditRegional(row)}
                          className="text-blast-600 dark:text-blast-400 hover:underline text-sm inline-flex items-center gap-1"
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(row)}
                          className="text-red-600 dark:text-red-400 hover:underline text-sm inline-flex items-center gap-1"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add picker */}
                {availableRegions.length > 0 && !addingRegionId && (
                  <select
                    className="input mt-3"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) startAddRegional(e.target.value);
                    }}
                  >
                    <option value="">+ Add regional override…</option>
                    {availableRegions.map((nr) => (
                      <option key={nr.id} value={nr.id}>
                        {nr.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Inline add form */}
                {addingRegionId &&
                  (() => {
                    const region = namedRegions.find((r) => r.id === addingRegionId);
                    if (!region) return null;
                    return (
                      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 mt-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          Add override for {region.name} ({region.zip3s.length} ZIP-3
                          {region.zip3s.length === 1 ? '' : 's'})
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label htmlFor="add-override-season" className="label text-xs">Season</label>
                            <select
                              id="add-override-season"
                              className="input"
                              value={addingDraft.primarySeason ?? ''}
                              onChange={(e) =>
                                setAddingDraft({
                                  ...addingDraft,
                                  primarySeason: (e.target.value || null) as Season | null,
                                })
                              }
                            >
                              <option value="">(none)</option>
                              {SEASONS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="add-override-confidence" className="label text-xs">Confidence</label>
                            <select
                              id="add-override-confidence"
                              className="input"
                              value={addingDraft.confidence}
                              onChange={(e) =>
                                setAddingDraft({
                                  ...addingDraft,
                                  confidence: e.target.value as Confidence,
                                })
                              }
                            >
                              {CONFIDENCES.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={addingDraft.sanctioned}
                                onChange={(e) =>
                                  setAddingDraft({ ...addingDraft, sanctioned: e.target.checked })
                                }
                              />
                              Sanctioned
                            </label>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-2">
                          <button type="button" className="btn-secondary" onClick={cancelAdd}>
                            Cancel
                          </button>
                          <button type="button" className="btn-primary" onClick={saveAddRegional}>
                            Create
                          </button>
                        </div>
                      </div>
                    );
                  })()}
              </>
            )}
          </div>

          {/* Footer (hidden while the dry-run confirmation card is showing) */}
          {!confirming && (
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={attemptSave}>
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete regional override?"
        message={
          pendingDelete
            ? `Delete the regional override for ${
                namedRegions.find((r) => r.zip3s.some((z) => z.zip3 === pendingDelete.zip3Prefix))?.name ??
                `ZIP-3 ${pendingDelete.zip3Prefix}`
              }? This affects only the regional default — existing Sport rows in that ZIP-3 keep their current season until the state-wide default propagates again.`
            : ''
        }
        confirmLabel="Delete"
        danger
        onConfirm={confirmDeleteRegional}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
